// ─────────────────────────────────────────────────────────────
// SimplePagination – standalone pagination control
// ─────────────────────────────────────────────────────────────
export interface SimplePaginationProps {
  totalItems: number;
  itemsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  perPageOptions?: number[];
}

export default function SimplePagination({
  totalItems,
  itemsPerPage,
  currentPage,
  onPageChange,
  onPerPageChange,
  perPageOptions = [10, 25, 50],
}: SimplePaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const from = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const to = Math.min(currentPage * itemsPerPage, totalItems);

  const pages: (number | "...")[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (currentPage > 3) pages.push("...");
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push("...");
    pages.push(totalPages);
  }

  const btnBase =
    "inline-flex items-center justify-center h-8 min-w-[2rem] rounded-md text-sm font-medium transition-colors focus:outline-none";
  const btnActive   = `${btnBase} bg-blue-600 text-white`;
  const btnInactive = `${btnBase} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 px-2 cursor-pointer`;
  const btnDisabled = `${btnBase} border border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed px-2`;
  const selectCls   = "border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:border-blue-400 cursor-pointer";

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-3">
      <p className="text-xs text-gray-500 whitespace-nowrap">
        {totalItems === 0
          ? "Nenhum resultado"
          : `Exibindo ${from}–${to} de ${totalItems}`}
      </p>

      <div className="flex items-center gap-1">
        <button type="button" disabled={currentPage === 1} onClick={() => onPageChange(1)}
          className={currentPage === 1 ? btnDisabled : btnInactive} title="Primeira página">«</button>
        <button type="button" disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)}
          className={currentPage === 1 ? btnDisabled : btnInactive} title="Página anterior">‹</button>

        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`ellipsis-${i}`} className="text-gray-400 px-1 text-sm select-none">…</span>
          ) : (
            <button key={p} type="button" onClick={() => onPageChange(p as number)}
              className={p === currentPage ? btnActive : btnInactive}>{p}</button>
          )
        )}

        <button type="button" disabled={currentPage === totalPages} onClick={() => onPageChange(currentPage + 1)}
          className={currentPage === totalPages ? btnDisabled : btnInactive} title="Próxima página">›</button>
        <button type="button" disabled={currentPage === totalPages} onClick={() => onPageChange(totalPages)}
          className={currentPage === totalPages ? btnDisabled : btnInactive} title="Última página">»</button>
      </div>

      {onPerPageChange && (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>Itens por página:</span>
          <select
            value={itemsPerPage}
            onChange={e => onPerPageChange(Number(e.target.value))}
            className={selectCls}
          >
            {perPageOptions.map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
